// Utility to extract a human-friendly occasion phrase from free-form text
// Returns composed phrases like "Summer Wedding", "Office Holiday Party",
// "Summer Black-Tie Wedding", etc.

const EVENTS = [
  // Multi-word first for greedy matching
  "date night",
  "cocktail party",
  "holiday party",
  "baby shower",
  "bridal shower",
  // Single word
  "wedding",
  "reception",
  "ceremony",
  "interview",
  "meeting",
  "party",
  "brunch",
  "funeral",
  "graduation",
  "conference",
  "photoshoot",
  "festival",
  "presentation",
  "date",
];

const SEASONS = ["summer", "winter", "spring", "fall", "autumn"] as const;
const TIMES = ["evening", "night", "morning", "day"] as const;
const LOCATIONS = [
  "beach",
  "garden",
  "rooftop",
  "outdoor",
  "indoor",
  "office",
  "work",
] as const;
const FORMALITY = [
  "black tie",
  "semi formal",
  "semi-formal",
  "smart casual",
  "formal",
  "semi",
  "casual",
  "dressy",
] as const;

// Basic synonyms to normalize event detection
const SYNONYMS: Record<string, string> = {
  nuptials: "wedding",
  reception: "reception",
  ceremony: "ceremony",
  photoshoot: "photoshoot",
  "photo shoot": "photoshoot",
};

const STOPWORDS = new Set([
  "for",
  "to",
  "wear",
  "in",
  "at",
  "on",
  "the",
  "a",
  "an",
  "my",
  "our",
  "your",
  "this",
  "that",
]);

// Fallback broad mapping similar to prior implementation
const BROAD_MAPPINGS: Array<{ patterns: RegExp[]; occasion: string }> = [
  { patterns: [/\b(work|office|meeting|professional|business)\b/i], occasion: "work" },
  { patterns: [/\b(casual|relaxed|everyday|comfortable)\b/i], occasion: "casual" },
  { patterns: [/\b(date\s*night|date|romantic|dinner|restaurant)\b/i], occasion: "date" },
  { patterns: [/\b(party|celebration|event|social)\b/i], occasion: "party" },
  { patterns: [/\b(formal|elegant|fancy|dressy)\b/i], occasion: "formal" },
  { patterns: [/\b(vacation|travel|holiday|trip)\b/i], occasion: "vacation" },
  { patterns: [/\b(gym|workout|exercise|fitness)\b/i], occasion: "workout" },
  { patterns: [/\b(beach|outdoor|picnic)\b/i], occasion: "outdoor" },
  { patterns: [/\b(summer)\b/i], occasion: "summer" },
  { patterns: [/\b(winter|cold|warm|cozy)\b/i], occasion: "winter" },
  { patterns: [/\b(brunch|lunch|breakfast|coffee)\b/i], occasion: "brunch" },
];

function titleCasePhrase(phrase: string): string {
  // Hyphenation rules for select terms
  const hyphenations: Record<string, string> = {
    "black tie": "Black-Tie",
    "semi formal": "Semi-Formal",
    "semi-formal": "Semi-Formal",
  };

  const normalized = phrase.trim().toLowerCase();
  if (hyphenations[normalized]) return hyphenations[normalized];

  return normalized
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function findCandidateSpan(text: string): string {
  const lower = text.toLowerCase();
  // Prefer segment after common introducers
  const introducers = [
    /\bto wear to\b\s*(.+)/i,
    /\bfor\b\s*(.+)/i,
    /\bto\b\s*(.+)/i,
  ];
  for (const rx of introducers) {
    const m = lower.match(rx);
    if (m && m[1]) return m[1].split(/[.!?]/)[0];
  }
  return lower;
}

function normalizeWord(word: string): string {
  const trimmed = word.trim().toLowerCase();
  return SYNONYMS[trimmed] || trimmed;
}

function containsPhrase(hay: string, needle: string): boolean {
  const rx = new RegExp(`(^|[^a-z])${needle.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}([^a-z]|$)`, "i");
  return rx.test(hay);
}

export function extractOccasion(message: string): string {
  const candidate = findCandidateSpan(message);

  // 1) Detect event (prefer longer phrases)
  const sortedEvents = [...EVENTS].sort((a, b) => b.length - a.length);
  let event: string | null = null;
  for (const e of sortedEvents) {
    if (containsPhrase(candidate, e)) {
      event = SYNONYMS[e] || e;
      break;
    }
  }

  // 2) Gather modifiers anywhere in candidate
  const foundSeasons = SEASONS.filter((s) => containsPhrase(candidate, s));
  const foundLocations = LOCATIONS.filter((l) => containsPhrase(candidate, l));
  const foundTimesAll = TIMES.filter((t) => containsPhrase(candidate, t));
  const foundFormalityAll = FORMALITY.filter((f) => containsPhrase(candidate, f));

  // Avoid duplicating modifiers that are already part of the event phrase
  const eventNorm = (event || "").toLowerCase();
  const foundTimes = foundTimesAll.filter((t) => !eventNorm.includes(t));
  const foundFormality = foundFormalityAll.filter((f) => !eventNorm.includes(f.replace(/-/g, " ")));

  // 3) Compose in a consistent order
  const parts: string[] = [];
  if (foundSeasons[0]) parts.push(titleCasePhrase(foundSeasons[0]));
  if (foundLocations[0]) parts.push(titleCasePhrase(foundLocations[0]));
  if (foundFormality[0]) parts.push(titleCasePhrase(foundFormality[0]));
  if (event) parts.push(titleCasePhrase(event));
  if (foundTimes[0]) parts.push(titleCasePhrase(foundTimes[0]));

  if (parts.length > 0) return parts.join(" ");

  // 4) Fallbacks
  for (const m of BROAD_MAPPINGS) {
    if (m.patterns.some((p) => p.test(message))) return m.occasion;
  }

  // Final fallback to cleaned message (limited length to avoid noisy prompts)
  const cleaned = message
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w && !STOPWORDS.has(w))
    .slice(0, 6)
    .join(" ");
  return cleaned ? titleCasePhrase(cleaned) : "";
}

export default extractOccasion;
